import React, { useEffect, useMemo, useRef, useState } from 'react';
import ShopifyTagModeControl from '../components/ShopifyTagModeControl';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Collapse,
  alpha,
  Tooltip,
  Stack,
  LinearProgress,
  Radio,
  RadioGroup,
  FormLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Sync,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  ExpandMore,
  Storefront,
  ShoppingCart,
  ShoppingBag,
  Bolt,
  ViewInAr,
  Widgets,
  ShoppingBasket,
  CloudSync,
  Settings,
  History,
  GridView,
  LinkOff,
  Link as LinkIcon,
  Restore,
  Map as MapIcon,
  Inventory2,
  AttachMoney,
  Image as ImageIcon,
  LocalOffer,
  Schedule,
  SyncAlt,
  Category,
  InfoOutlined,
  EditOutlined,
  VisibilityOutlined,
  ContentCopy,
  MoreVert,
  Circle,
  Key,
  Tune,
  Receipt,
} from '@mui/icons-material';
import { shopifyMappingHref } from './integrationDetailTabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  integrationApiService,
  IntegrationConfig,
  IntegrationPlatform,
  IntegrationSettings,
  IntegrationTestResult,
  FieldMapping,
  SyncMode,
  StoreLayoutSettings,
} from '../services/integrationApi';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import IntegrationExternalLinks from '../components/IntegrationExternalLinks';
import ContinueSellingRow from '../components/ContinueSellingRow';
import ProPublicSyncSettingsSection from '../components/ProPublicSyncSettingsSection';
import OverrideFieldsPicker from '../components/OverrideFieldsPicker';
import ShopifySetupChecklist from '../components/ShopifySetupChecklist';
import ShopifyCategoryMappingSection from '../components/ShopifyCategoryMappingSection';
import ShopifyPricingZonesSection from '../components/ShopifyPricingZonesSection';
import ShopifyDataCollectionNotice from '../components/ShopifyDataCollectionNotice';
import IntegrationSetupWalkthrough from '../components/integrationSetup/IntegrationSetupWalkthrough';
import { installDestination } from '../components/integrationSetup/installDestination';
import ShopifyDescriptionTemplateSection from '../components/ShopifyDescriptionTemplateSection';
import { SyncModeSection } from '../components/IntegrationSettingsSections';
import { LightspeedDiscountChoice } from '../components/lightspeed/LightspeedSettingsSections';
import { LightspeedEcomCredentialsForm, LightspeedEcomCredentialsReadView, LIGHTSPEED_ECOM_DEFAULT_LANGUAGE } from '../components/lightspeedEcom/LightspeedEcomCredentialsForm';
import LightspeedEcomSettingsSection, { enabledLightspeedEcomFields } from '../components/lightspeedEcom/LightspeedEcomSettingsSection';
import LightspeedEcomScanCard from '../components/lightspeedEcom/LightspeedEcomScanCard';
import LightspeedCatalogMatch from '../components/lightspeed/LightspeedCatalogMatch';
import { rebateDiscountOn } from '../components/integrationSetup/rebateDiscountSync';
import { lightspeedCatalogModes } from '../components/integrationSetup/walkthroughSteps';
import IntegrationInstallWizard, { InstallerLaunch, LIGHTSPEED_INSTALL_PREFS_KEY } from '../components/IntegrationInstallWizard';
import { IntegrationLimitNotice } from '../components/integrations/IntegrationLimitNotice';
import { isAtIntegrationLimit, type IntegrationCapacity } from '../components/integrations/integrationLimitModel';
import { resolveShopifyConnectMethod } from '../config/shopifyPublicAppInstall';
import ShopifyPendingCustomApps from '../components/ShopifyPendingCustomApps';
import ShopifyScopeGatedSwitch from '../components/ShopifyScopeGatedSwitch';
import ShopifyRedirectPermissionAlert from '../components/ShopifyRedirectPermissionAlert';
import StorefrontPresetsCard from '../components/StorefrontPresetsCard';
import StorefrontPresetGallery from '../components/storefrontPresets/StorefrontPresetGallery';
import { ConfirmDialog, NumberField, PageContainer, PageHeader } from '../components/ui';
import { validateUrl } from '../utils/inputValidators';
import { canManageIntegrationCredentials, credentialsEdited, maskedSecret, savedSecret } from '../utils/integrationCredentials';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { INTEGRATION_SETTINGS_COPY as COPY, syncModeChipLabel } from '../utils/integrationSettingsCopy';
import { AIHelpButton } from '../components/AIHelpButton';
import { useAIAgent } from '../contexts/AIAgentContext';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useOrganizations } from '../contexts/OrganizationContext';
import { tokens } from '../theme/tokens';

const PLATFORM_OPTIONS: { value: IntegrationPlatform; label: string; icon: React.ReactNode; logo: string }[] = [
  { value: 'bigcommerce', label: 'BigCommerce', icon: <Storefront />, logo: '/logos/platforms/bigcommerce.svg' },
  { value: 'woocommerce', label: 'WooCommerce', icon: <ShoppingCart />, logo: '/logos/platforms/woocommerce.svg' },
  { value: 'lightspeed', label: 'Lightspeed', icon: <Bolt />, logo: '/logos/platforms/lightspeed.svg' },
  // Flag `lightspeed_ecom_connector`: shown in the picker only while the flag is on for the organization.
  { value: 'lightspeed_ecom', label: 'Lightspeed eCom (C-Series)', icon: <Storefront />, logo: '/logos/platforms/lightspeed.svg' },
  { value: 'shopify', label: 'Shopify', icon: <ShoppingBag />, logo: '/logos/platforms/shopify.svg' },
  { value: 'magento', label: 'Magento', icon: <ViewInAr />, logo: '/logos/platforms/magento.svg' },
  { value: 'prestashop', label: 'PrestaShop', icon: <Widgets />, logo: '/logos/platforms/prestashop.svg' },
  { value: 'opencart', label: 'OpenCart', icon: <ShoppingBasket />, logo: '/logos/platforms/opencart.svg' },
];

/**
 * Magento, PrestaShop and OpenCart have no working connector (mirrors the backend's
 * BILLABLE_INTEGRATION_PLATFORMS). They stay in PLATFORM_OPTIONS so an already-connected
 * doc still shows its name and logo, but are hidden from every picker and cannot be synced.
 */
const UNSUPPORTED_PLATFORMS: IntegrationPlatform[] = ['magento', 'prestashop', 'opencart'];
const isUnsupportedPlatform = (platform: IntegrationPlatform | undefined): boolean =>
  !!platform && UNSUPPORTED_PLATFORMS.includes(platform);
const SELECTABLE_PLATFORM_OPTIONS = PLATFORM_OPTIONS.filter((p) => !isUnsupportedPlatform(p.value));

/**
 * BigCommerce and WooCommerce do not sync until the selection-based push is rebuilt (MOT-283):
 * offered for a new connection only while flag `bigcommerce_woocommerce_connect` is on for the
 * organization (the API refuses the connection otherwise). A connected one still lists normally.
 */
const CONNECT_FLAGGED_PLATFORMS: IntegrationPlatform[] = ['bigcommerce', 'woocommerce'];

/**
 * Platforms with an automated connect flow. When present, guided setup leads with the
 * connect button and tucks manual credential entry behind an "advanced" expander.
 */
const PLATFORM_AUTOMATED_CONNECT: Partial<Record<IntegrationPlatform, { buttonLabel: string; description: string }>> = {
  shopify: {
    buttonLabel: 'Connect with Shopify',
    description:
      'Recommended: install the Motzu app via Shopify OAuth — we handle API access and tokens automatically. Manual Custom App credentials below are for advanced setups.',
  },
};

export const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const { actingOrganization: pageActingOrganization } = useOrganizations();
  const { isImpersonating, impersonatedRole, impersonatedOrg } = useImpersonation();
  const viewingAsStore = isImpersonating && impersonatedRole === 'store';
  // Reconnecting replaces the stored Shopify token: owner/admin only, like every credential write (MOT-249).
  const canManageCredentials = canManageIntegrationCredentials(pageActingOrganization?.role, (userProfile as { role?: string } | null)?.role);
  const organizationId = userProfile?.organizationId || (userProfile as any)?.primaryOrganizationId || null;
  const handledShopifyReturnRef = useRef(false);
  const handledLightspeedReturnRef = useRef(false);
  const [installerLaunch, setInstallerLaunch] = useState<InstallerLaunch | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<IntegrationPlatform | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<IntegrationConfig | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [manualCredentialsIntent, setManualCredentialsIntent] = useState(false);
  const [syncingIntegrationId, setSyncingIntegrationId] = useState<string | null>(null);
  const [syncingSalesId, setSyncingSalesId] = useState<string | null>(null);
  const [lightspeedFullResyncTarget, setLightspeedFullResyncTarget] = useState<IntegrationConfig | null>(null);
  const [lightspeedReconnectingId, setLightspeedReconnectingId] = useState<string | null>(null);
  const { showToast, hideToast } = useToast();
  const { t } = useAppTranslation();
  const { isEnabled: isOrgFlagEnabled, isFetched: orgFlagsFetched } = useOrgFeatureFlags();
  const perIntegrationSyncSelectionEnabled = isOrgFlagEnabled('per_integration_sync_selection');
  const lightspeedSalesSyncEnabled = isOrgFlagEnabled('lightspeed_sales_sync');
  const lightspeedEcomEnabled = isOrgFlagEnabled('lightspeed_ecom_connector');
  const listingMergeEnabled = isOrgFlagEnabled('variant_listing_consolidation');
  const bigcommerceWooConnectEnabled = isOrgFlagEnabled('bigcommerce_woocommerce_connect');
  const isPickablePlatform = (platform: IntegrationPlatform): boolean =>
    (platform !== 'lightspeed_ecom' || lightspeedEcomEnabled)
    && (!CONNECT_FLAGGED_PLATFORMS.includes(platform) || bigcommerceWooConnectEnabled);
  const setupWalkthroughEnabled = isOrgFlagEnabled('integration_setup_walkthrough');
  // The integration whose setup walkthrough is open (a snapshot; the live row from the list wins when present).
  const [walkthroughTarget, setWalkthroughTarget] = useState<IntegrationConfig | null>(null);
  type SyncFeedback = { integrationId: string; severity: 'success' | 'info' | 'error'; message: string };
  const showSyncFeedback = ({ integrationId, severity, message }: SyncFeedback) =>
    showToast(message, severity, {
      autoHideMs: 10000,
      action: integrationId ? (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            navigate(`/app/integrations/${integrationId}`);
            hideToast();
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          View Activity
        </Button>
      ) : undefined,
    });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const syncStartRef = useRef<number>(0);

  const normalizeShopId = (value: string): string => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return '';
    const withoutProtocol = trimmed.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    return withoutProtocol.replace(/\.myshopify\.com$/, '');
  };

  const shopifyIntegrationIdForShop = (shop: string): string | undefined => {
    const normalized = normalizeShopId(shop);
    if (!normalized || !integrations) return undefined;
    return integrations.find((integration) => {
      if (integration.platform !== 'shopify') return false;
      const credentials = integration.credentials || {};
      return normalizeShopId(String(credentials.shopId || credentials.shopDomain || '')) === normalized;
    })?.id;
  };

  const queryClient = useQueryClient();

  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: integrationApiService.getIntegrations,
  });

  // Same numbers the OAuth start routes enforce. A failed read leaves the buttons
  // in place; those routes still refuse before any provider redirect (MOT-808).
  const { data: integrationCapacity } = useQuery({
    queryKey: ['integration-capacity', impersonatedOrg?.id || pageActingOrganization?.id || 'self'],
    queryFn: () => integrationApiService.getIntegrationCapacity(),
    staleTime: 30 * 1000,
  });
  const atIntegrationLimit = isAtIntegrationLimit(integrationCapacity);

  // Which integrations keep their own product selection, for the card chip.
  const { data: syncSelections } = useQuery({
    queryKey: ['store-sync-selections'],
    queryFn: () => apiService.getStoreSyncSelections(),
    enabled: perIntegrationSyncSelectionEnabled,
    staleTime: 30 * 1000,
  });

  const customSelectionByIntegration = useMemo(() => {
    const byId = new Map<string, number>();
    for (const row of syncSelections || []) {
      if (row.integrationId && !row.inheritsFromOrganization) {
        byId.set(row.integrationId, row.productCount);
      }
    }
    return byId;
  }, [syncSelections]);
  // Second-connection banner: a store with an all-systems sheet gets a
  // one-time nudge once it has two or more connected integrations, since an
  // all-locations sheet now prices the newly connected platform too.
  const secondConnectionBannerKey = organizationId ? `motzu:secondConnectionBannerDismissed:${organizationId}` : null;
  const [secondConnectionBannerDismissed, setSecondConnectionBannerDismissed] = useState(false);
  useEffect(() => {
    if (!secondConnectionBannerKey) return;
    try {
      setSecondConnectionBannerDismissed(localStorage.getItem(secondConnectionBannerKey) === '1');
    } catch {
      // Private window / blocked storage: never block the page on this.
    }
  }, [secondConnectionBannerKey]);
  const dismissSecondConnectionBanner = () => {
    setSecondConnectionBannerDismissed(true);
    if (!secondConnectionBannerKey) return;
    try {
      localStorage.setItem(secondConnectionBannerKey, '1');
    } catch {
      // Ignored — the banner just won't stay dismissed across reloads.
    }
  };

  const hasTwoOrMoreIntegrations = (integrations?.length || 0) >= 2;
  const { data: pricingSheetsSummary } = useQuery({
    queryKey: ['pricing-sheets-summary'],
    queryFn: async () => {
      const res = await makeApiCall('/store/pricing/sheets-summary', { headers: await getAuthHeaders() });
      if (!res.ok) return null;
      const json = await res.json();
      return json.data as { allSystems: number; scoped: number; privateToStore: number } | null;
    },
    enabled: hasTwoOrMoreIntegrations,
  });
  const newestIntegration = useMemo(() => {
    if (!integrations || integrations.length < 2) return null;
    return [...integrations].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
  }, [integrations]);
  const showSecondConnectionBanner =
    !secondConnectionBannerDismissed && hasTwoOrMoreIntegrations && (pricingSheetsSummary?.allSystems || 0) > 0;

  const createMutation = useMutation({
    mutationFn: integrationApiService.createIntegration,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integration-capacity'] });
      setDialogOpen(false);
      // A BigCommerce / WooCommerce (or pasted-credential) connection has no
      // OAuth return, so this is its connect end state: walk the store through
      // the settings the credentials form did not ask about.
      if (setupWalkthroughEnabled && created?.id) setWalkthroughTarget(created);
    },
    onError: (error) => {
      console.error('Failed to create integration:', error);
      showSyncFeedback({ integrationId: '', severity: 'error', message: 'Failed to create integration: ' + ((error as any)?.message || 'Unknown error') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<IntegrationConfig> }) =>
      integrationApiService.updateIntegration(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setDialogOpen(false);
    },
    onError: (error) => {
      console.error('Failed to update integration:', error);
      showSyncFeedback({ integrationId: '', severity: 'error', message: 'Failed to update integration: ' + ((error as any)?.message || 'Unknown error') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: integrationApiService.deleteIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integration-capacity'] });
    },
  });

  const clearSyncingAfterDelay = (feedback: SyncFeedback) => {
    const elapsed = Date.now() - syncStartRef.current;
    const remaining = Math.max(0, 1800 - elapsed);
    setTimeout(() => {
      setSyncingIntegrationId(null);
      showSyncFeedback(feedback);
    }, remaining);
  };

  const syncMutation = useMutation({
    mutationFn: async (id: string) => {
      syncStartRef.current = Date.now();
      setSyncingIntegrationId(id);
      const result = await integrationApiService.syncIntegration(id);
      return { result, id };
    },
    onSuccess: ({ result, id }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      const status = result?.status;
      let feedback: SyncFeedback;
      if (status === 'queued' || status === 'running') {
        const label = result.existing ? 'Sync already in progress' : `Sync ${status}`;
        feedback = { integrationId: id, severity: 'info', message: `${label} — the background worker will process it shortly.` };
      } else if (status === 'success' || status === 'partial') {
        feedback = { integrationId: id, severity: 'success', message: `Sync complete — ${result.productsProcessed || 0} products processed, ${result.productsCreated || 0} created, ${result.productsUpdated || 0} updated` };
      } else if (status === 'error') {
        feedback = { integrationId: id, severity: 'error', message: `Sync failed${result.errors?.length ? `: ${result.errors[0]}` : ''}` };
      } else {
        feedback = { integrationId: id, severity: 'info', message: 'Sync has been queued and will run shortly.' };
      }
      clearSyncingAfterDelay(feedback);
    },
    onError: (error: any, id) => {
      const msg = error?.message || 'Unknown error';
      clearSyncingAfterDelay({ integrationId: id, severity: 'error', message: `Sync failed: ${msg}` });
    },
  });

  const testMutation = useMutation({
    mutationFn: async ({
      platform,
      credentials,
      storedIntegrationId,
    }: {
      platform: IntegrationPlatform;
      credentials: Record<string, any>;
      storedIntegrationId?: string;
    }) => {
      // A saved integration is tested on the server with the credentials it stores (the API never
      // sends its secrets to the browser); whatever the store retyped is merged over them there.
      if (storedIntegrationId) return integrationApiService.testIntegration(storedIntegrationId, credentials);
      // Lightspeed eCom before it is saved: a credential-only check that never creates (and so never
      // bills, reconciles or deletes) an integration.
      if (platform === 'lightspeed_ecom') return integrationApiService.testLightspeedEcomCredentials(credentials);
      // For testing before creation, we'll create a temporary integration and test it
      const tempIntegration = {
        platform,
        name: `Test ${platform} Integration`,
        credentials,
        settings: {
          syncProducts: false,
          syncInventory: false,
          syncPricing: false,
          syncImages: false,
          addMotzuTag: true,
          autoSync: false,
          syncInterval: 'manual' as const,
          fieldMappings: [],
        },
        isActive: false, // Don't activate the test integration
      };
      
      // Create the integration first
      const createdIntegration = await integrationApiService.createIntegration(tempIntegration);
      
      // Test the integration, then always delete the temporary one
      try {
        return await integrationApiService.testIntegration(createdIntegration.id);
      } finally {
        await integrationApiService.deleteIntegration(createdIntegration.id).catch(() => undefined);
      }
    },
    onSuccess: (result) => {
      setTestResult(result);
      setIsTesting(false);
    },
    onError: () => {
      setIsTesting(false);
    },
  });

  // Reconnect an existing Shopify store so it grants scopes added since it
  // was installed. Only a store installed through Motzu's public app can be
  // re-run through public OAuth; a custom-app OAuth install re-enters its own
  // client id and secret, and a store that pasted an Admin API token adds the
  // scopes in its Shopify app configuration and re-checks. Sending those
  // through public OAuth would replace their token with a public-app one.
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);
  const shopifyInstallMethod = (integration: IntegrationConfig): 'public_oauth' | 'custom_app_oauth' | 'manual_token' => {
    const method = integration.credentials?.installMethod;
    if (method === 'public_oauth' || method === 'custom_app_oauth') return method;
    if (integration.credentials?.customApp === true) return 'custom_app_oauth';
    // Installs before installMethod was recorded: treat as a pasted token
    // unless we know otherwise, since the wrong guess overwrites a token.
    return 'manual_token';
  };
  const shopifyReconnectMutation = useMutation({
    mutationFn: ({
      integration,
      scopeGroups,
    }: {
      integration: IntegrationConfig;
      scopeGroups?: Array<'inventory' | 'orders' | 'discounts' | 'markets'>;
    }) => {
      const shop = String(integration.credentials?.shopId || integration.credentials?.shopDomain || '');
      const method = shopifyInstallMethod(integration);
      if (method !== 'public_oauth') {
        // Their client id/secret are not stored client-side; open the install dialog pre-filled instead.
        throw new Error('custom_app');
      }
      return integrationApiService.startShopifyInstall(shop, scopeGroups?.length ? { scopeGroups } : undefined);
    },
    onMutate: ({ integration }) => setReconnectingId(integration.id),
    onSuccess: (data) => {
      window.location.assign(data.installUrl);
    },
    onError: (error, { integration }) => {
      setReconnectingId(null);
      if ((error as Error)?.message === 'custom_app') {
        setInstallerLaunch({
          kind: 'shopify',
          shop: String(integration.credentials?.shopId || ''),
          method: 'custom',
          reconnectIntegrationId: integration.id,
          error:
            'This store was connected with its own Shopify app. Add the new permissions to that app in Shopify, then either click "Check permissions" on the card (Admin API token) or enter the app\'s Client ID and Secret here to reconnect.',
        });
        return;
      }
      showToast((error as Error)?.message || 'Failed to start reconnect', 'error');
    },
  });
  const shopifyRefreshScopesMutation = useMutation({
    mutationFn: (integrationId: string) => integrationApiService.refreshShopifyScopes(integrationId),
    onSuccess: (status, integrationId) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      const checked = (integrations || []).find((row) => row.id === integrationId);
      // Motzu's own app already asks for write_content, so reconnecting grants it; a store's own app
      // or pasted token has to add it in Shopify first.
      const redirectsHint = checked && shopifyInstallMethod(checked) === 'public_oauth'
        ? 'Catalog permissions are in place. Redirects for merged listings still need write_content: reconnect to grant it.'
        : 'Catalog permissions are in place. Redirects for merged listings still need write_content: add it to your Shopify app, then check again.';
      showToast(
        status.needsReauth
          ? `Missing ${status.missing.length} catalog permission${status.missing.length === 1 ? '' : 's'}; reconnect to grant them`
          : status.redirectsMissing && listingMergeEnabled
            ? redirectsHint
            : 'Catalog permissions are in place. Inventory and order sync stay locked until Shopify approves them.',
        status.needsReauth ? 'warning' : 'success',
      );
    },
    onError: (error) => showToast((error as Error)?.message || 'Failed to check permissions', 'error'),
  });

  // Lightspeed sales pull (lightspeed_sales_sync). Incremental by default; fullResync is only
  // reached through the confirm dialog below since it re-reads all history.
  const lightspeedSalesSyncMutation = useMutation({
    mutationFn: ({ id, fullResync }: { id: string; fullResync?: boolean }) =>
      integrationApiService.syncLightspeedSales(id, { fullResync }),
    onMutate: ({ id }) => setSyncingSalesId(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (result.errors?.length) {
        showToast(t('integrations.lightspeedSales.syncFailed', { message: result.errors[0] }), 'error');
      } else {
        showToast(
          t('integrations.lightspeedSales.syncSuccess', {
            orders: result.ordersProcessed || 0,
            lines: result.lineItemsSynced || 0,
          }),
          'success',
        );
      }
    },
    onError: (error) => showToast(t('integrations.lightspeedSales.syncFailed', { message: (error as Error)?.message || 'Unknown error' }), 'error'),
    onSettled: () => setSyncingSalesId(null),
  });

  // Reconnect through the existing Lightspeed OAuth flow, mirroring the Shopify reconnect above.
  const lightspeedReconnectMutation = useMutation({
    mutationFn: (integrationId: string) => {
      sessionStorage.removeItem(LIGHTSPEED_INSTALL_PREFS_KEY);
      return integrationApiService.startLightspeedInstall({ reconnectIntegrationId: integrationId });
    },
    onSuccess: (data) => {
      window.location.assign(data.installUrl);
    },
    onError: (error) => {
      setLightspeedReconnectingId(null);
      showToast((error as Error)?.message || 'Failed to start Lightspeed reconnect', 'error');
    },
  });

  const handleCreateIntegration = () => {
    setSelectedPlatform(null);
    setInstallerLaunch({ kind: 'picker' });
  };

  const handlePickCredentialPlatform = (platform: IntegrationPlatform) => {
    if (atIntegrationLimit) {
      setInstallerLaunch({ kind: 'picker' });
      return;
    }
    setInstallerLaunch(null);
    setManualCredentialsIntent(false);
    setSelectedPlatform(platform);
    setEditingIntegration(null);
    setDialogOpen(true);
    setActiveTab(0);
  };

  const handleStartAutomatedConnect = (platform: IntegrationPlatform) => {
    if (platform === 'shopify') {
      setDialogOpen(false);
      setInstallerLaunch({ kind: 'shopify', method: resolveShopifyConnectMethod('app') });
    }
  };

  const handleEditIntegration = (integration: IntegrationConfig) => {
    setSelectedPlatform(null);
    setEditingIntegration(integration);
    setDialogOpen(true);
    setActiveTab(0);
  };

  const handleDeleteIntegration = (id: string) => {
    setDeleteTargetId(id);
  };

  const handleSyncIntegration = (id: string) => {
    syncMutation.mutate(id);
  };

  const getStatusColor = (integration: IntegrationConfig) => {
    if (!integration.isActive) return 'default';
    if (integration.lastSyncSummary?.runErrorCode) return 'error';
    if (integration.lastSync) return 'success';
    return 'warning';
  };

  const getStatusText = (integration: IntegrationConfig) => {
    if (!integration.isActive) return 'Inactive';
    // The latest run stopped on something only the store can fix (e.g. a read-only API key, MOT-796).
    if (integration.lastSyncSummary?.runErrorCode) return 'Needs attention';
    if (integration.lastSync) return 'Synced';
    return 'Not Synced';
  };

  const formatLastSyncDate = (value: unknown): string => {
    if (!value) return 'Never';

    // Handle Firestore Timestamp-like values from API responses.
    if (typeof value === 'object') {
      const record = value as Record<string, any>;
      if (typeof record.toDate === 'function') {
        const date = record.toDate();
        if (date instanceof Date && !Number.isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }

      const seconds =
        typeof record.seconds === 'number'
          ? record.seconds
          : typeof record._seconds === 'number'
          ? record._seconds
          : null;
      if (seconds !== null) {
        const date = new Date(seconds * 1000);
        if (!Number.isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }
    }

    const date = new Date(value as any);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleDateString();
  };

  const { openWithContext } = useAIAgent();

  const handleAIHelp = () => {
    openWithContext?.('Help me set up and configure integrations');
  };

  useEffect(() => {
    // Wait for the flags so the walkthrough-or-settings choice below is made once, on real values.
    if (!integrations || !orgFlagsFetched || handledShopifyReturnRef.current) return;

    const searchParams = new URLSearchParams(location.search);
    const shopifyStatus = searchParams.get('shopify');
    const returnedShop = normalizeShopId(searchParams.get('shop') || '');

    if (!shopifyStatus || !returnedShop) return;
    handledShopifyReturnRef.current = true;

    if (shopifyStatus === 'connected') {
      const matchingIntegration = integrations.find((integration) => {
        if (integration.platform !== 'shopify') return false;
        const credentials = integration.credentials || {};
        const credentialShopId = normalizeShopId(
          String(credentials.shopId || credentials.shopDomain || '')
        );
        return credentialShopId === returnedShop;
      });

      if (matchingIntegration) {
        if (setupWalkthroughEnabled && !matchingIntegration.setupWalkthrough?.completedAt) {
          setWalkthroughTarget(matchingIntegration);
        } else {
          setSelectedPlatform(null);
          setEditingIntegration(matchingIntegration);
          setDialogOpen(true);
          setActiveTab(2);
        }
        showToast(
          'Shopify connected. One more look in Shopify: taxes and shipping profiles stay there. The checklist in Sync Behavior has the links.',
          'info',
        );
      }
    } else if (shopifyStatus === 'error') {
      // A saved custom app's install stopped on Shopify's side; its card says why, and the
      // wizard reopens on the saved app so the store only has to try again.
      queryClient.invalidateQueries({ queryKey: ['shopify-custom-apps'] });
      showToast(t('integrations.customAppResume.returnedError', { shop: returnedShop }), 'error');
      const existingId = shopifyIntegrationIdForShop(returnedShop);
      setInstallerLaunch({
        kind: 'shopify',
        shop: returnedShop,
        method: 'custom',
        ...(existingId ? { reconnectIntegrationId: existingId } : {}),
      });
    }

    const cleanSearch = new URLSearchParams(location.search);
    cleanSearch.delete('shopify');
    cleanSearch.delete('shop');
    navigate(
      {
        pathname: location.pathname,
        search: cleanSearch.toString() ? `?${cleanSearch.toString()}` : '',
      },
      { replace: true }
    );
  }, [integrations, orgFlagsFetched, setupWalkthroughEnabled, location.pathname, location.search, navigate, showToast, queryClient, t]);

  // Handle return from the Lightspeed OAuth flow
  useEffect(() => {
    if (!integrations || !orgFlagsFetched || handledLightspeedReturnRef.current) return;

    const searchParams = new URLSearchParams(location.search);
    const lightspeedStatus = searchParams.get('lightspeed');
    if (!lightspeedStatus) return;
    handledLightspeedReturnRef.current = true;

    if (lightspeedStatus === 'connected') {
      const returnedAccount = searchParams.get('account') || '';
      const matchingIntegration = integrations.find((integration) => {
        if (integration.platform !== 'lightspeed') return false;
        if (!returnedAccount) return true;
        const credentials = integration.credentials || {};
        return String(credentials.accountId || credentials.accountID || '') === returnedAccount;
      });

      if (matchingIntegration) {
        const rawPrefs = sessionStorage.getItem(LIGHTSPEED_INSTALL_PREFS_KEY);
        sessionStorage.removeItem(LIGHTSPEED_INSTALL_PREFS_KEY);
        // Await prefs before opening Sync Behavior so Save cannot overwrite them with
        // the stale pre-OAuth snapshot still in matchingIntegration. Lightspeed is
        // the POS source of truth for inventory, so only the sales choice carries over.
        void (async () => {
          let integrationForDialog: IntegrationConfig = matchingIntegration;
          let prefs: { sales?: boolean } = {};
          try {
            prefs = rawPrefs ? (JSON.parse(rawPrefs) as { sales?: boolean }) : {};
          } catch {
            // Unreadable prefs fall back to the integration's own settings.
          }
          if (lightspeedSalesSyncEnabled && prefs.sales === true) {
            try {
              const headers = await getAuthHeaders();
              const response = await makeApiCall(`/integrations/${matchingIntegration.id}/order-sync`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderSyncEnabled: true }),
              });
              if (!response.ok) throw new Error(`Request failed (${response.status})`);
              integrationForDialog = { ...integrationForDialog, orderSyncEnabled: true };
              await queryClient.invalidateQueries({ queryKey: ['integrations'] });
            } catch {
              showSyncFeedback({
                integrationId: matchingIntegration.id,
                severity: 'error',
                message: 'Lightspeed connected, but sales sync could not be turned on. Turn it on in Sync Behavior.',
              });
            }
          }
          if (setupWalkthroughEnabled && !integrationForDialog.setupWalkthrough?.completedAt) {
            setWalkthroughTarget(integrationForDialog);
            return;
          }
          setSelectedPlatform(null);
          setEditingIntegration(integrationForDialog);
          setDialogOpen(true);
          setActiveTab(2);
        })();
      }
      showSyncFeedback({
        integrationId: matchingIntegration?.id || '',
        severity: 'success',
        message: 'Lightspeed account connected successfully.',
      });
    } else if (lightspeedStatus === 'error') {
      showSyncFeedback({
        integrationId: '',
        severity: 'error',
        message: `Lightspeed connect failed: ${searchParams.get('message') || 'Unknown error'}`,
      });
    }

    const cleanSearch = new URLSearchParams(location.search);
    cleanSearch.delete('lightspeed');
    cleanSearch.delete('account');
    cleanSearch.delete('message');
    navigate(
      {
        pathname: location.pathname,
        search: cleanSearch.toString() ? `?${cleanSearch.toString()}` : '',
      },
      { replace: true }
    );
  }, [integrations, orgFlagsFetched, setupWalkthroughEnabled, lightspeedSalesSyncEnabled, location.pathname, location.search, navigate, queryClient]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const configureId = searchParams.get('configure');
    if (!configureId || !integrations) return;
    const found = integrations.find((item) => item.id === configureId);
    if (!found) return;
    // Existing-integration tabs: 0 Overview, 1 Credentials, 2 Settings.
    // Default to Settings (store layout / matching); credentials Edit passes tab=credentials.
    const activeConfigureTab = searchParams.get('tab') === 'credentials' ? 1 : 2;
    setSelectedPlatform(null);
    setEditingIntegration(found);
    setDialogOpen(true);
    setActiveTab(activeConfigureTab);
    const cleanSearch = new URLSearchParams(location.search);
    cleanSearch.delete('configure');
    cleanSearch.delete('tab');
    navigate(
      {
        pathname: location.pathname,
        search: cleanSearch.toString() ? `?${cleanSearch.toString()}` : '',
      },
      { replace: true },
    );
  }, [integrations, location.pathname, location.search, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContainer maxWidth={1100}>
      <PageHeader
        title="Integrations"
        description="Connect your camera data with e-commerce platforms to automatically sync products, inventory, and pricing."
        actions={
          <>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateIntegration}>
              Add Integration
            </Button>
            <AIHelpButton onClick={handleAIHelp} variant="button" label="Ask Mozi" />
          </>
        }
      />

      {atIntegrationLimit && (
        <IntegrationLimitNotice capacity={integrationCapacity} viewingAsAdmin={viewingAsStore} />
      )}

      {showSecondConnectionBanner && pricingSheetsSummary && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }} onClose={dismissSecondConnectionBanner}>
          {pricingSheetsSummary.allSystems} pricing sheet{pricingSheetsSummary.allSystems === 1 ? '' : 's'} apply to all your
          systems and will now price {PLATFORM_OPTIONS.find((p) => p.value === newestIntegration?.platform)?.label || 'this platform'} too.
          Keep them separate by location or integration with Custom Pricing.
        </Alert>
      )}

      <ShopifyPendingCustomApps
        canManage={canManageCredentials}
        onContinue={(shopId) => {
          const existingId = shopifyIntegrationIdForShop(shopId);
          setInstallerLaunch({
            kind: 'shopify',
            shop: shopId,
            method: 'custom',
            ...(existingId ? { reconnectIntegrationId: existingId } : {}),
          });
        }}
        onContinueSetup={(shopId) => {
          const existingId = shopifyIntegrationIdForShop(shopId);
          setInstallerLaunch({
            kind: 'shopify',
            shop: shopId,
            method: 'custom',
            openCustomAppWizard: true,
            ...(existingId ? { reconnectIntegrationId: existingId } : {}),
          });
        }}
      />

      {/* Integrations List */}
      {integrations && integrations.length > 0 ? (
        <Stack spacing={2}>
          {integrations.map((integration) => {
            const platformOpt = PLATFORM_OPTIONS.find(p => p.value === integration.platform);
            const statusText = getStatusText(integration);
            const isActive = integration.isActive;
            const isSynced = statusText === 'Synced';
            const statusColor = !isActive
              ? tokens.ink.faint
              : isSynced
                ? tokens.signal.positive.solid
                : statusText === 'Needs attention'
                  ? tokens.signal.problem.solid
                  : tokens.signal.caution.solid;
            const shortId = String(integration.credentials?.shopId || integration.credentials?.accountId || integration.credentials?.storeHash || '');
            return (
              <Paper
                key={integration.id}
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                  borderRadius: `${tokens.radius.panel}px`,
                  boxShadow: tokens.shadow.card,
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': { boxShadow: tokens.shadow.menu, borderColor: tokens.line.strong },
                  ...(syncingIntegrationId === integration.id && {
                    borderColor: 'primary.main',
                    boxShadow: (theme: any) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}, 0 2px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                  }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Box sx={{ width: 4, bgcolor: statusColor, flexShrink: 0 }} />

                  <Box sx={{ flex: 1, px: 2.5, pt: 2.25, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, mb: 1 }}>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: '10px', display: 'grid', placeItems: 'center',
                        bgcolor: tokens.surface.sunken, flexShrink: 0,
                      }}>
                        <Box
                          component="img"
                          src={platformOpt?.logo}
                          alt=""
                          sx={{ maxHeight: 28, maxWidth: 34, objectFit: 'contain' }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            component="button"
                            onClick={() => navigate(`/app/integrations/${integration.id}`)}
                            sx={{
                              border: 'none',
                              background: 'none',
                              p: 0,
                              cursor: 'pointer',
                              fontSize: 15.5,
                              fontWeight: 700,
                              color: tokens.ink.primary,
                              textAlign: 'left',
                              minWidth: 0,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {integration.name.replace(/\s*\(.*?\)\s*$/, '')}
                          </Typography>
                          {shortId && (
                            <Typography sx={{ fontSize: 12, fontWeight: 500, color: tokens.ink.faint, flexShrink: 0 }} noWrap>
                              {shortId}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{platformOpt?.label}</Typography>
                          <Typography variant="caption" color="text.disabled">·</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Circle sx={{ fontSize: 7, color: statusColor }} />
                            <Typography variant="caption" sx={{ color: statusColor, fontWeight: 600 }}>
                              {statusText}
                            </Typography>
                          </Box>
                          {integration.lastSync && (
                            <>
                              <Typography variant="caption" color="text.disabled">·</Typography>
                              <Typography variant="caption" color="text.disabled">
                                Last sync {formatLastSyncDate(integration.lastSync)}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Quick info chips */}
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
                      {isUnsupportedPlatform(integration.platform) && (
                        <Chip label="Not supported — not synced or billed" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }} />
                      )}
                      {integration.platform === 'lightspeed_ecom' ? (
                        // Content-only connector: the content fields it writes, not the generic data-sync switches.
                        enabledLightspeedEcomFields(integration.settings).map((key) => (
                          <Chip key={key} label={t(`integrations.lightspeedEcom.settings.fields.${key}.label`)} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />
                        ))
                      ) : (
                        <>
                          {integration.settings?.syncProducts !== false && <Chip label="Products" size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />}
                          {integration.settings?.syncPricing !== false && <Chip label="Pricing" size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />}
                          {integration.settings?.syncImages !== false && <Chip label="Images" size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />}
                          {integration.settings?.syncInventory && <Chip label="Inventory" size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />}
                        </>
                      )}
                      {integration.settings?.autoSync && <Chip label={`Auto · ${integration.settings.syncInterval || 'manual'}`} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />}
                      {customSelectionByIntegration.has(integration.id) && (
                        <Chip
                          label={`Custom selection · ${customSelectionByIntegration.get(integration.id)} products`}
                          size="small"
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }}
                        />
                      )}
                      {setupWalkthroughEnabled && !integration.setupWalkthrough?.completedAt && (
                        <Chip
                          label={t('integrations.setupWalkthrough.finishSetup')}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={(event) => {
                            event.stopPropagation();
                            setWalkthroughTarget(integration);
                          }}
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                        />
                      )}
                      {integration.settings?.syncMode && (
                        <Chip label={syncModeChipLabel(integration.platform, integration.settings.syncMode)} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }} />
                      )}
                      {integration.platform === 'lightspeed' && lightspeedSalesSyncEnabled && (
                        <Chip
                          label={
                            integration.salesSync?.lastSyncAt
                              ? t('integrations.lightspeedSales.lastSync', { date: formatLastSyncDate(integration.salesSync.lastSyncAt) })
                              : t('integrations.lightspeedSales.neverSynced')
                          }
                          size="small"
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt }}
                        />
                      )}
                    </Box>

                    {integration.platform === 'lightspeed' && lightspeedSalesSyncEnabled && integration.salesSyncAuthRequired && (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1.5, py: 0.25, alignItems: 'center' }}
                        action={
                          // Re-authorizing replaces the stored tokens: owner/admin only (MOT-249).
                          canManageCredentials ? (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => {
                                setLightspeedReconnectingId(integration.id);
                                lightspeedReconnectMutation.mutate(integration.id);
                              }}
                              disabled={lightspeedReconnectingId === integration.id}
                            >
                              {lightspeedReconnectingId === integration.id
                                ? 'Opening Lightspeed…'
                                : t('integrations.lightspeedSales.reauthButton')}
                            </Button>
                          ) : (
                            <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                              {t('integrations.savedCredentials.adminOnly')}
                            </Typography>
                          )
                        }
                      >
                        <strong>{t('integrations.lightspeedSales.reauthTitle')}</strong> {t('integrations.lightspeedSales.reauthMessage')}
                      </Alert>
                    )}

                    {integration.platform === 'lightspeed' &&
                      lightspeedSalesSyncEnabled &&
                      !integration.salesSyncAuthRequired &&
                      integration.salesSync?.lastError && (
                        <Alert severity="error" sx={{ mt: 1.5, py: 0.25, alignItems: 'center' }}>
                          {t('integrations.lightspeedSales.lastError', { message: integration.salesSync.lastError })}
                        </Alert>
                      )}

                    {integration.platform === 'shopify' && integration.scopeStatus && integration.scopeStatus.needsReauth !== false && (
                      <Alert
                        severity={integration.scopeStatus.needsReauth ? 'warning' : 'info'}
                        sx={{ mt: 1.5, py: 0.25, alignItems: 'center' }}
                        action={
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {integration.scopeStatus.needsReauth && !canManageCredentials && (
                              <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                                {t('integrations.savedCredentials.adminOnly')}
                              </Typography>
                            )}
                            {integration.scopeStatus.needsReauth && canManageCredentials && (
                              <Button
                                size="small"
                                color="inherit"
                                onClick={() => shopifyReconnectMutation.mutate({ integration })}
                                disabled={reconnectingId === integration.id}
                              >
                                {reconnectingId === integration.id
                                  ? 'Opening Shopify…'
                                  : shopifyInstallMethod(integration) === 'public_oauth'
                                    ? 'Reconnect'
                                    : 'How to reconnect'}
                              </Button>
                            )}
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => shopifyRefreshScopesMutation.mutate(integration.id)}
                              disabled={shopifyRefreshScopesMutation.isPending}
                            >
                              {shopifyRefreshScopesMutation.isPending ? 'Checking…' : 'Check permissions'}
                            </Button>
                          </Box>
                        }
                      >
                        {integration.scopeStatus.needsReauth ? (
                          <>
                            <strong>Reconnect to grant new permissions.</strong>{' '}
                            Motzu now needs{' '}
                            {integration.scopeStatus.requirements
                              .filter((r) => integration.scopeStatus!.missing.includes(r.scope))
                              .map((r) => r.reason.toLowerCase())
                              .join(', ')}
                            . Existing sync keeps working until you do.
                            {shopifyInstallMethod(integration) !== 'public_oauth' && (
                              <> This store uses its own Shopify app: add the permissions there, then check again.</>
                            )}
                          </>
                        ) : (
                          <>Motzu has added permissions since this store connected. Check whether it needs to reconnect.</>
                        )}
                      </Alert>
                    )}

                    {integration.platform === 'shopify' && (
                      <ShopifyRedirectPermissionAlert
                        scopeStatus={integration.scopeStatus}
                        installMethod={shopifyInstallMethod(integration)}
                        mergeEnabled={listingMergeEnabled}
                        canManageCredentials={canManageCredentials}
                        adminOnlyLabel={t('integrations.savedCredentials.adminOnly')}
                        reconnecting={reconnectingId === integration.id}
                        checking={shopifyRefreshScopesMutation.isPending}
                        onReconnect={() => shopifyReconnectMutation.mutate({ integration })}
                        onCheckPermissions={() => shopifyRefreshScopesMutation.mutate(integration.id)}
                      />
                    )}

                    {/* Actions row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, mt: 2, mx: -2.5, px: 2.5, borderTop: '1px solid', borderColor: tokens.line.hairline, py: 1.25, alignItems: 'center', '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                      <Button size="small" startIcon={<GridView />} onClick={() => navigate(`/app/integrations/${integration.id}`)} sx={{ color: tokens.ink.bodyAlt, fontWeight: 600, fontSize: 13 }}>
                        Overview
                      </Button>
                      <Button size="small" startIcon={<History />} onClick={() => navigate(`/app/integrations/${integration.id}?tab=history`)} sx={{ color: tokens.ink.bodyAlt, fontWeight: 600, fontSize: 13 }}>
                        History
                      </Button>
                      <IntegrationExternalLinks integration={integration} compact />
                      <Tooltip title="Run sync now">
                        <Button
                          size="small"
                          startIcon={syncingIntegrationId === integration.id ? <CircularProgress size={14} /> : <Sync />}
                          onClick={() => handleSyncIntegration(integration.id)}
                          disabled={syncingIntegrationId === integration.id || isUnsupportedPlatform(integration.platform)}
                          sx={{
                            color: syncingIntegrationId === integration.id ? 'primary.main' : 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          {syncingIntegrationId === integration.id ? 'Syncing…' : 'Sync Now'}
                        </Button>
                      </Tooltip>
                      {integration.platform === 'lightspeed' && lightspeedSalesSyncEnabled && (
                        <>
                          <Tooltip title={t('integrations.lightspeedSales.syncButton')}>
                            <Button
                              size="small"
                              startIcon={syncingSalesId === integration.id ? <CircularProgress size={14} /> : <Receipt />}
                              onClick={() => lightspeedSalesSyncMutation.mutate({ id: integration.id })}
                              disabled={syncingSalesId === integration.id}
                              sx={{
                                color: syncingSalesId === integration.id ? 'primary.main' : 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                              }}
                            >
                              {syncingSalesId === integration.id
                                ? t('integrations.lightspeedSales.syncing')
                                : t('integrations.lightspeedSales.syncButton')}
                            </Button>
                          </Tooltip>
                          <Tooltip title={t('integrations.lightspeedSales.fullResyncTooltip')}>
                            <IconButton
                              size="small"
                              onClick={() => setLightspeedFullResyncTarget(integration)}
                              disabled={syncingSalesId === integration.id}
                              sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                              aria-label={t('integrations.lightspeedSales.fullResyncButton')}
                            >
                              <Restore fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Button size="small" startIcon={<Tune />} onClick={() => navigate(`/app/integrations/${integration.id}?tab=sync-behavior`)} sx={{ color: tokens.ink.bodyAlt, fontWeight: 600, fontSize: 13 }}>
                        Configure
                      </Button>
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title="Delete integration">
                        <IconButton size="small" onClick={() => handleDeleteIntegration(integration.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* Syncing progress indicator */}
                    {syncingIntegrationId === integration.id && (
                      <LinearProgress sx={{ mt: 1.5, mx: -2.5, mb: -2.5, borderRadius: '0 0 10px 10px' }} />
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, px: 3, borderRadius: `${tokens.radius.panel}px` }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              mx: 'auto',
              mb: 2,
              borderRadius: `${tokens.radius.card}px`,
              bgcolor: tokens.surface.tint,
              display: 'grid',
              placeItems: 'center',
              color: tokens.ink.muted,
            }}
          >
            <CloudSync />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
            No integrations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Connect your first e-commerce platform to start syncing your camera data automatically.
          </Typography>
          <Button variant="outlined" onClick={handleCreateIntegration}>
            Choose a platform
          </Button>
        </Paper>
      )}

      {/* Integration Dialog */}
      <IntegrationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        integration={editingIntegration}
        initialPlatform={selectedPlatform}
        onSave={(integration) => {
          if (editingIntegration) {
            return updateMutation.mutateAsync({ id: editingIntegration.id, updates: integration });
          }
          return createMutation.mutateAsync(integration as any);
        }}
        onTest={(platform, credentials, storedIntegrationId) => {
          setIsTesting(true);
          testMutation.mutate({ platform, credentials, storedIntegrationId });
        }}
        testResult={testResult}
        isTesting={isTesting}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onStartAutomatedConnect={handleStartAutomatedConnect}
        startWithManualCredentials={manualCredentialsIntent}
        onGrantShopifyScope={
          canManageCredentials
            ? (group) => {
                if (!editingIntegration) return;
                shopifyReconnectMutation.mutate({ integration: editingIntegration, scopeGroups: [group] });
              }
            : undefined
        }
        grantingShopifyScope={editingIntegration ? reconnectingId === editingIntegration.id : false}
        shopifyCustomApp={
          editingIntegration ? shopifyInstallMethod(editingIntegration) !== 'public_oauth' : false
        }
        integrationCapacity={integrationCapacity}
        viewingAsAdmin={viewingAsStore}
      />

      <IntegrationSetupWalkthrough
        open={walkthroughTarget !== null}
        integration={
          walkthroughTarget ? integrations?.find((item) => item.id === walkthroughTarget.id) ?? walkthroughTarget : null
        }
        onClose={() => setWalkthroughTarget(null)}
        onFinished={() => {
          setWalkthroughTarget(null);
          showToast(t('integrations.setupWalkthrough.finished'), 'success');
        }}
      />

      <IntegrationInstallWizard
        launch={installerLaunch}
        onClose={() => setInstallerLaunch(null)}
        platforms={SELECTABLE_PLATFORM_OPTIONS.filter((p) => isPickablePlatform(p.value)).map(({ value, label, logo }) => ({ value, label, logo }))}
        onPickCredentialPlatform={handlePickCredentialPlatform}
        capacity={integrationCapacity}
        viewingAsAdmin={viewingAsStore}
        onScanCatalog={(integration) => {
          setInstallerLaunch(null);
          const destination = installDestination(integration, setupWalkthroughEnabled);
          if (destination === 'walkthrough') {
            setWalkthroughTarget(integration);
            return;
          }
          // The Shopify scan lives in the integration's Mapping tab (Catalog Matching), not in the settings dialog.
          if (destination === 'mapping') {
            navigate(shopifyMappingHref(integration.id, 'listing-matches'));
            return;
          }
          setEditingIntegration(integration);
          setSelectedPlatform(null);
          setDialogOpen(true);
          setActiveTab(2);
        }}
      />


      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Delete Integration"
        message="Are you sure you want to delete this integration? This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTargetId) {
            deleteMutation.mutate(deleteTargetId, { onSettled: () => setDeleteTargetId(null) });
          }
        }}
        onClose={() => setDeleteTargetId(null)}
      />

      <ConfirmDialog
        open={lightspeedFullResyncTarget !== null}
        title={t('integrations.lightspeedSales.confirmTitle')}
        message={t('integrations.lightspeedSales.confirmMessage')}
        confirmLabel={t('integrations.lightspeedSales.confirmButton')}
        confirmColor="warning"
        loading={lightspeedSalesSyncMutation.isPending}
        onConfirm={() => {
          if (lightspeedFullResyncTarget) {
            lightspeedSalesSyncMutation.mutate(
              { id: lightspeedFullResyncTarget.id, fullResync: true },
              { onSettled: () => setLightspeedFullResyncTarget(null) },
            );
          }
        }}
        onClose={() => setLightspeedFullResyncTarget(null)}
      />
    </PageContainer>
  );
};

interface IntegrationDialogProps {
  open: boolean;
  onClose: () => void;
  integration: IntegrationConfig | null;
  initialPlatform?: IntegrationPlatform | null;
  onSave: (integration: Partial<IntegrationConfig>) => void | Promise<unknown>;
  /** `storedIntegrationId` tests a saved integration server-side with what it stores. */
  onTest: (platform: IntegrationPlatform, credentials: Record<string, any>, storedIntegrationId?: string) => void;
  testResult: IntegrationTestResult | null;
  isTesting: boolean;
  activeTab: number;
  onTabChange: (tab: number) => void;
  /** Launches the platform's automated connect flow (e.g. Shopify OAuth install). */
  onStartAutomatedConnect?: (platform: IntegrationPlatform) => void;
  /** When true, the manual credentials section starts expanded in guided setup. */
  startWithManualCredentials?: boolean;
  onGrantShopifyScope?: (group: 'inventory' | 'orders' | 'discounts' | 'markets') => void;
  grantingShopifyScope?: boolean;
  shopifyCustomApp?: boolean;
  /** Plan allowance. A new integration shows the limit notice instead of a credential form. */
  integrationCapacity?: IntegrationCapacity | null;
  viewingAsAdmin?: boolean;
}

const IntegrationDialog: React.FC<IntegrationDialogProps> = ({
  open,
  onClose,
  integration,
  initialPlatform,
  onSave,
  onTest,
  testResult,
  isTesting,
  activeTab,
  onTabChange,
  onStartAutomatedConnect,
  startWithManualCredentials,
  onGrantShopifyScope,
  grantingShopifyScope,
  shopifyCustomApp,
  integrationCapacity,
  viewingAsAdmin,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const { userProfile } = useAuth();
  const { actingOrganization } = useOrganizations();
  // Credentials decide where the stored secrets go, so only an owner or admin (or a platform admin)
  // may change them; the API refuses everyone else (MOT-249).
  const canManageCredentials = canManageIntegrationCredentials(actingOrganization?.role, (userProfile as { role?: string } | null)?.role);
  const isGuidedSetup = !integration && !!initialPlatform;
  const credentialsTab = isGuidedSetup ? 0 : 1;
  const settingsTab = isGuidedSetup ? 1 : 2;
  const selectedPlatformLabel = initialPlatform
    ? PLATFORM_OPTIONS.find((option) => option.value === initialPlatform)?.label || 'Integration'
    : 'Integration';
  const [storeUrlTouched, setStoreUrlTouched] = useState(false);
  const [formData, setFormData] = useState({
    platform: '' as IntegrationPlatform,
    name: '',
    isActive: true,
    credentials: {} as Record<string, any>,
    settings: {
      syncProducts: true,
      syncInventory: false,
      syncPricing: true,
      syncImages: true,
      addMotzuTag: true,
      autoSync: false,
      syncInterval: 'manual' as 'manual' | 'hourly' | 'daily' | 'weekly',
      syncMode: 'override' as SyncMode,
      defaultInventoryCount: undefined as number | undefined,
      continueSellingWhenOutOfStock: false,
      groupKitBodyAsVariants: true,
      allowProPublicSync: false,
      fieldMappings: [] as FieldMapping[],
      storeLayout: undefined as StoreLayoutSettings | undefined,
    } as IntegrationSettings,
  });

  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [orderSyncEnabled, setOrderSyncEnabled] = useState(false);
  const [showManualCredentials, setShowManualCredentials] = useState(true);
  const [lightspeedOAuthPending, setLightspeedOAuthPending] = useState(false);
  const [lightspeedOAuthError, setLightspeedOAuthError] = useState<string | null>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { isEnabled: isOrgFlagEnabled } = useOrgFeatureFlags();
  const lightspeedLinkOnlyEnabled = isOrgFlagEnabled('lightspeed_link_only');
  const lightspeedCatalogParityEnabled = isOrgFlagEnabled('lightspeed_catalog_parity');
  const lightspeedModes =
    formData.platform === 'lightspeed'
      ? lightspeedCatalogModes({
          lightspeed_link_only: lightspeedLinkOnlyEnabled,
          lightspeed_catalog_parity: lightspeedCatalogParityEnabled,
        })
      : null;
  const dialogBigcommerceWooConnectEnabled = isOrgFlagEnabled('bigcommerce_woocommerce_connect');
  const perIntegrationSyncSelectionEnabled = isOrgFlagEnabled('per_integration_sync_selection');

  // Products to sync: the store's selection by default, or this integration's own.
  const selectionIntegrationId = integration?.id || null;
  const { data: integrationSyncSelection } = useQuery({
    queryKey: ['store-sync-selection', selectionIntegrationId],
    queryFn: () => apiService.getStoreSyncSelection(selectionIntegrationId),
    enabled: open && !!selectionIntegrationId && perIntegrationSyncSelectionEnabled,
    staleTime: 30 * 1000,
  });
  const usesOwnSelection = integrationSyncSelection?.inheritsFromOrganization === false;
  const selectionInheritMutation = useMutation({
    mutationFn: (inherit: boolean) =>
      apiService.setIntegrationSyncSelectionInheritance(selectionIntegrationId as string, inherit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selections'] });
    },
    onError: () => showToast('Could not change the products this integration syncs.', 'error'),
  });

  const handleLightspeedOAuth = async () => {
    const reconnecting = integration?.platform === 'lightspeed';
    if (!reconnecting && isAtIntegrationLimit(integrationCapacity)) {
      setLightspeedOAuthError(t('integrations.integrationLimit.title'));
      return;
    }
    setLightspeedOAuthPending(true);
    setLightspeedOAuthError(null);
    try {
      const { installUrl } = await integrationApiService.startLightspeedInstall(
        reconnecting && integration ? { reconnectIntegrationId: integration.id } : undefined,
      );
      window.location.assign(installUrl);
    } catch (error) {
      setLightspeedOAuthError((error as Error)?.message || 'Failed to start Lightspeed connect');
      setLightspeedOAuthPending(false);
    }
  };

  React.useEffect(() => {
    if (integration) {
      setFormData({
        platform: integration.platform,
        name: integration.name,
        isActive: integration.isActive,
        credentials: integration.credentials,
        settings: {
          ...integration.settings,
          addMotzuTag: integration.settings.addMotzuTag !== false,
          syncMode: integration.settings.syncMode || 'override',
          defaultInventoryCount: integration.settings.defaultInventoryCount,
          continueSellingWhenOutOfStock: integration.settings.continueSellingWhenOutOfStock === true,
          groupKitBodyAsVariants: integration.settings.groupKitBodyAsVariants !== false,
          allowProPublicSync: integration.settings.allowProPublicSync === true,
          storeLayout: integration.settings.storeLayout,
        } as IntegrationSettings,
      });
      setOrderSyncEnabled(integration.orderSyncEnabled === true);
      setIsEditingBasic(false);
      setIsEditingCredentials(false);
    } else {
      const seededPlatform = initialPlatform || ('' as IntegrationPlatform);
      const seededLabel = PLATFORM_OPTIONS.find((option) => option.value === seededPlatform)?.label;
      setFormData({
        platform: seededPlatform,
        name: seededLabel ? `${seededLabel} Integration` : '',
        isActive: true,
        credentials: {},
        settings: {
          syncProducts: true,
          syncInventory: false,
          syncPricing: true,
          syncImages: true,
          addMotzuTag: true,
          autoSync: false,
          syncInterval: 'manual' as 'manual' | 'hourly' | 'daily' | 'weekly',
          syncMode: 'override' as SyncMode,
          defaultInventoryCount: undefined,
          continueSellingWhenOutOfStock: false,
          groupKitBodyAsVariants: true,
          allowProPublicSync: false,
          fieldMappings: [] as FieldMapping[],
          storeLayout: undefined,
        } as IntegrationSettings,
      });
      setShowManualCredentials(
        !PLATFORM_AUTOMATED_CONNECT[seededPlatform] || startWithManualCredentials === true
      );
    }
  }, [integration, initialPlatform, startWithManualCredentials]);

  const handleSave = async () => {
    const credentials: Record<string, any> = { ...formData.credentials, apiVersion: '2026-04' };
    // Creating via this dialog is always a pasted Admin API token (OAuth goes
    // through the install modal). Flag it so Reconnect does not install Motzu's
    // public app over the custom token.
    if (formData.platform === 'shopify' && !integration) {
      credentials.customApp = true;
    }
    // An existing integration's credentials are sent only when they differ from what was loaded (the
    // editor may have been opened and closed with "Done", so whether it is open says nothing), and only
    // by someone allowed to change them; a plain settings save leaves them alone.
    const { credentials: _unchanged, ...withoutCredentials } = formData;
    const sendCredentials =
      !integration || (canManageCredentials && credentialsEdited(integration.credentials, formData.credentials));
    const payload = sendCredentials ? { ...formData, credentials } : withoutCredentials;
    const saved = onSave(payload);
    if (
      integration?.id &&
      formData.platform === 'lightspeed' &&
      isOrgFlagEnabled('lightspeed_sales_sync') &&
      rebateDiscountOn(integration.settings.rebateDiscounts, 'instant') !==
        rebateDiscountOn(formData.settings.rebateDiscounts, 'instant')
    ) {
      void Promise.resolve(saved)
        .then(() => integrationApiService.syncLightspeedRebateDiscounts(integration.id))
        .catch(() => null);
    }

    if (integration && orderSyncEnabled !== (integration.orderSyncEnabled === true)) {
      try {
        const headers = await getAuthHeaders();
        await makeApiCall(`/integrations/${integration.id}/order-sync`, {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderSyncEnabled }),
        });
      } catch {
        // settings save already succeeded; order-sync toggle is best-effort
      }
    }
  };

  const handleTest = () => {
    // Before the first save the store typed every value, so the temporary-integration test runs;
    // a saved integration is tested on the server with its stored secrets.
    onTest(formData.platform, formData.credentials, integration?.id);
  };

  // Write-only credential fields: the API sends `<field>Set` / `<field>Last4`, never the value.
  const secretDisplay = (field: string) =>
    maskedSecret(formData.credentials, field, t('integrations.savedCredentials.saved'));
  const keepSavedHint = (field: string): string | undefined => {
    const { saved, last4 } = savedSecret(formData.credentials, field);
    if (!saved) return undefined;
    return last4
      ? t('integrations.savedCredentials.keepSaved', { last4 })
      : t('integrations.savedCredentials.keepSavedNoHint');
  };

  const renderCredentialField = (label: string, value: string | undefined) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 140 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, color: value ? 'text.primary' : 'text.disabled', letterSpacing: 0.3 }}>
        {value || '—'}
      </Typography>
    </Box>
  );

  const renderCredentialsReadView = () => {
    switch (formData.platform) {
      case 'shopify':
        return (
          <Box>
            {renderCredentialField('Shop ID', formData.credentials.shopId)}
            <Divider />
            {renderCredentialField('Shop Domain', formData.credentials.shopDomain || (formData.credentials.shopId ? `${formData.credentials.shopId}.myshopify.com` : undefined))}
            <Divider />
            {renderCredentialField('Admin API Token', secretDisplay('accessToken'))}
            <Divider />
            {renderCredentialField('API Version', '2026-04')}
          </Box>
        );
      case 'bigcommerce':
        return (
          <Box>
            {renderCredentialField('Store Hash', formData.credentials.storeHash)}
            <Divider />
            {renderCredentialField('Access Token', secretDisplay('accessToken'))}
            <Divider />
            {renderCredentialField('Client ID', formData.credentials.clientId)}
            <Divider />
            {renderCredentialField('Client Secret', secretDisplay('clientSecret'))}
          </Box>
        );
      case 'woocommerce':
        return (
          <Box>
            {renderCredentialField('Store URL', formData.credentials.storeUrl)}
            <Divider />
            {renderCredentialField('Consumer Key', secretDisplay('consumerKey'))}
            <Divider />
            {renderCredentialField('Consumer Secret', secretDisplay('consumerSecret'))}
          </Box>
        );
      case 'lightspeed_ecom':
        return <LightspeedEcomCredentialsReadView credentials={formData.credentials} renderField={renderCredentialField} />;
      case 'lightspeed':
        return (
          <Box>
            {renderCredentialField('Account ID', formData.credentials.accountId)}
            <Divider />
            {renderCredentialField('Access Token', secretDisplay('accessToken'))}
            <Divider />
            {renderCredentialField('Refresh Token', secretDisplay('refreshToken'))}
            <Divider />
            {renderCredentialField('Client ID', formData.credentials.clientId)}
            <Divider />
            {renderCredentialField('Client Secret', secretDisplay('clientSecret'))}
          </Box>
        );
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Credential details for {formData.platform} are not available.
          </Typography>
        );
    }
  };

  const renderCredentialsForm = () => {
    switch (formData.platform) {
      case 'bigcommerce':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Store Hash"
              value={formData.credentials.storeHash || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, storeHash: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Access Token"
              type="password"
              value={formData.credentials.accessToken || ''}
              helperText={keepSavedHint('accessToken')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, accessToken: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Client ID"
              value={formData.credentials.clientId || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, clientId: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Client Secret"
              type="password"
              value={formData.credentials.clientSecret || ''}
              helperText={keepSavedHint('clientSecret')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, clientSecret: e.target.value }
              })}
              fullWidth
            />
          </Box>
        );
      case 'woocommerce':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Store URL"
              value={formData.credentials.storeUrl || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, storeUrl: e.target.value }
              })}
              onBlur={() => setStoreUrlTouched(true)}
              error={Boolean(storeUrlError)}
              helperText={storeUrlError ?? undefined}
              fullWidth
            />
            <TextField
              label="Consumer Key"
              value={formData.credentials.consumerKey || ''}
              helperText={keepSavedHint('consumerKey')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, consumerKey: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Consumer Secret"
              type="password"
              value={formData.credentials.consumerSecret || ''}
              helperText={keepSavedHint('consumerSecret')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, consumerSecret: e.target.value }
              })}
              fullWidth
            />
          </Box>
        );
      case 'lightspeed_ecom':
        return (
          <LightspeedEcomCredentialsForm
            credentials={{ cluster: 'us1', language: LIGHTSPEED_ECOM_DEFAULT_LANGUAGE, ...formData.credentials }}
            onChange={(credentials) => setFormData({ ...formData, credentials })}
          />
        );
      case 'lightspeed':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Recommended: connect with Lightspeed OAuth — Motzu handles tokens and account discovery
              automatically. Manual R-Series credentials below are for advanced setups.
            </Alert>
            <Box>
              <Button
                variant="contained"
                onClick={handleLightspeedOAuth}
                disabled={lightspeedOAuthPending}
                startIcon={lightspeedOAuthPending ? <CircularProgress size={16} /> : <Bolt />}
              >
                {lightspeedOAuthPending ? 'Redirecting…' : 'Connect with Lightspeed'}
              </Button>
              {lightspeedOAuthError && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 1.5 }}>{lightspeedOAuthError}</Alert>
              )}
            </Box>
            <Divider>
              <Typography variant="caption" color="text.secondary">or enter credentials manually</Typography>
            </Divider>
            <TextField
              label="Account ID"
              value={formData.credentials.accountId || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, accountId: e.target.value.trim() }
              })}
              fullWidth
              helperText="Numeric account ID from Lightspeed Retail (GET /API/V3/Account.json)"
            />
            <TextField
              label="Access Token"
              type="password"
              value={formData.credentials.accessToken || ''}
              helperText={keepSavedHint('accessToken')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, accessToken: e.target.value }
              })}
              fullWidth
              required={!savedSecret(formData.credentials, 'accessToken').saved}
            />
            <TextField
              label="Refresh Token"
              type="password"
              value={formData.credentials.refreshToken || ''}
              helperText={keepSavedHint('refreshToken')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, refreshToken: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Client ID"
              value={formData.credentials.clientId || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, clientId: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="Client Secret"
              type="password"
              value={formData.credentials.clientSecret || ''}
              helperText={keepSavedHint('clientSecret')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, clientSecret: e.target.value }
              })}
              fullWidth
            />
          </Box>
        );
      case 'shopify':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isGuidedSetup && <ShopifyDataCollectionNotice />}
            <Alert severity="info">
              Supports OAuth install or manual Custom App credentials. For manual setup, use Shop ID + Admin API Access Token.
            </Alert>
            <TextField
              label="Shop ID"
              helperText="Example: tqk01a-pt"
              value={formData.credentials.shopId || formData.credentials.shopDomain || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: {
                  ...formData.credentials,
                  shopId: e.target.value
                    .trim()
                    .toLowerCase()
                    .replace(/^https?:\/\//, '')
                    .replace(/\/.*$/, '')
                    .replace(/\.myshopify\.com$/, ''),
                  shopDomain: e.target.value
                    .trim()
                    .toLowerCase()
                    .replace(/^https?:\/\//, '')
                    .replace(/\/.*$/, '')
                    .replace(/\.myshopify\.com$/, '')
                    ? `${e.target.value
                        .trim()
                        .toLowerCase()
                        .replace(/^https?:\/\//, '')
                        .replace(/\/.*$/, '')
                        .replace(/\.myshopify\.com$/, '')}.myshopify.com`
                    : '',
                }
              })}
              fullWidth
            />
            <TextField
              label="Shop Domain (Reference)"
              value={
                formData.credentials.shopDomain ||
                (formData.credentials.shopId
                  ? `${formData.credentials.shopId}.myshopify.com`
                  : '')
              }
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Admin API Access Token"
              type="password"
              value={formData.credentials.accessToken || ''}
              helperText={keepSavedHint('accessToken')}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, accessToken: e.target.value }
              })}
              fullWidth
            />
            <TextField
              label="API Version"
              value="2026-04"
              fullWidth
              disabled
              helperText="Managed by the platform — always uses the latest stable version"
            />
          </Box>
        );
      default:
        return (
          <Alert severity="info">
            Credential configuration for {formData.platform} will be available soon.
          </Alert>
        );
    }
  };

  const platformOpt = PLATFORM_OPTIONS.find(p => p.value === formData.platform);
  const storeUrlError = storeUrlTouched ? validateUrl(formData.credentials.storeUrl || '', { allowSchemeless: true }) : null;
  const automatedConnect = formData.platform ? PLATFORM_AUTOMATED_CONNECT[formData.platform] : undefined;
  const blockNewIntegration = !integration && isAtIntegrationLimit(integrationCapacity);

  if (blockNewIntegration) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>New Integration</DialogTitle>
        <DialogContent>
          <IntegrationLimitNotice capacity={integrationCapacity} viewingAsAdmin={viewingAsAdmin} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: 3 } }}>
      {/* Dialog header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          {platformOpt && (
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', '& .MuiSvgIcon-root': { fontSize: 18, color: 'text.secondary' } }}>
              {platformOpt.icon}
            </Box>
          )}
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {integration ? integration.name.replace(/\s*\(.*?\)\s*$/, '') : isGuidedSetup ? `Set Up ${selectedPlatformLabel}` : 'New Integration'}
          </Typography>
          {integration?.credentials?.shopId && (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: tokens.font.mono, fontSize: '0.7rem' }}>
              {integration.credentials.shopId}
            </Typography>
          )}
        </Box>
        {isGuidedSetup && (
          <Box sx={{ mb: 1, mt: 1 }}>
            <Stepper activeStep={activeTab === settingsTab ? 2 : 1} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}>
              <Step completed><StepLabel>Platform</StepLabel></Step>
              <Step><StepLabel>Credentials</StepLabel></Step>
              <Step><StepLabel>{t('integrations.detailTabs.syncBehavior')}</StepLabel></Step>
            </Stepper>
          </Box>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => onTabChange(newValue)}
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem' } }}
          >
            {!isGuidedSetup && <Tab icon={<InfoOutlined sx={{ fontSize: 16 }} />} iconPosition="start" label="Overview" />}
            <Tab icon={<Key sx={{ fontSize: 16 }} />} iconPosition="start" label="Credentials" />
            <Tab icon={<Tune sx={{ fontSize: 16 }} />} iconPosition="start" label={t('integrations.detailTabs.syncBehavior')} />
          </Tabs>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5 }}>
        {/* Overview Tab (edit mode) */}
        {!isGuidedSetup && activeTab === 0 && (
          <Box>
            {!isEditingBasic ? (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="overline">Integration Details</Typography>
                  <Button size="small" startIcon={<EditOutlined sx={{ fontSize: 14 }} />} onClick={() => setIsEditingBasic(true)} sx={{ fontSize: '0.8rem' }}>
                    Edit
                  </Button>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Platform</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {platformOpt?.icon && <Box sx={{ display: 'flex', '& .MuiSvgIcon-root': { fontSize: 16, color: 'text.secondary' } }}>{platformOpt.icon}</Box>}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{platformOpt?.label || formData.platform}</Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.name}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Status</Typography>
                    <Chip
                      size="small"
                      label={formData.isActive ? 'Active' : 'Inactive'}
                      color={formData.isActive ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 24, fontWeight: 500 }}
                    />
                  </Box>
                </Box>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="overline" color="primary.main">Editing Details</Typography>
                  <Button size="small" onClick={() => setIsEditingBasic(false)} sx={{ fontSize: '0.8rem' }}>
                    Done
                  </Button>
                </Box>
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Platform</InputLabel>
                    <Select value={formData.platform} label="Platform" onChange={(e) => setFormData({ ...formData, platform: e.target.value as IntegrationPlatform })}>
                      {PLATFORM_OPTIONS.filter((option) =>
                        option.value === formData.platform
                        || (!isUnsupportedPlatform(option.value)
                          && (!CONNECT_FLAGGED_PLATFORMS.includes(option.value) || dialogBigcommerceWooConnectEnabled))).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{option.icon}<Typography variant="body2">{option.label}</Typography></Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField label="Integration Name" size="small" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth />
                  <FormControlLabel
                    control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} size="small" />}
                    label={<Typography variant="body2">Active</Typography>}
                  />
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* Credentials Tab */}
        {activeTab === credentialsTab && (
          <Box>
            {formData.platform ? (
              <Box>
                {isGuidedSetup && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                    Step 2 of 3: Connect your {selectedPlatformLabel} account credentials.
                  </Alert>
                )}
                {!isEditingCredentials && !isGuidedSetup ? (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="overline">Connection Details</Typography>
                      {canManageCredentials ? (
                        <Button size="small" startIcon={<EditOutlined sx={{ fontSize: 14 }} />} onClick={() => setIsEditingCredentials(true)} sx={{ fontSize: '0.8rem' }}>
                          Edit
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('integrations.savedCredentials.adminOnly')}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ px: 2.5, py: 0.5 }}>
                      {renderCredentialsReadView()}
                    </Box>
                    <Box sx={{ px: 2.5, pb: 2 }}>
                      <Button variant="outlined" size="small" onClick={handleTest} disabled={isTesting} startIcon={isTesting ? <CircularProgress size={14} /> : <CheckCircle />} sx={{ fontWeight: 500 }}>
                        {isTesting ? 'Testing...' : 'Test Connection'}
                      </Button>
                      {testResult && (
                        <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1.5, borderRadius: 1.5 }}>{testResult.message}</Alert>
                      )}
                    </Box>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {!isGuidedSetup && (
                      <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="overline" color="primary.main">Editing Credentials</Typography>
                        <Button size="small" onClick={() => setIsEditingCredentials(false)} sx={{ fontSize: '0.8rem' }}>Done</Button>
                      </Box>
                    )}
                    <Box sx={{ p: 2.5 }}>
                      {isGuidedSetup && automatedConnect && (
                        <Box sx={{ mb: 1 }}>
                          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                            {automatedConnect.description}
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<Bolt />}
                            onClick={() => onStartAutomatedConnect?.(formData.platform)}
                          >
                            {automatedConnect.buttonLabel}
                          </Button>
                          <Divider sx={{ my: 2.5 }}>
                            <Button
                              size="small"
                              onClick={() => setShowManualCredentials((prev) => !prev)}
                              startIcon={
                                <ExpandMore
                                  sx={{
                                    transform: showManualCredentials ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s ease',
                                  }}
                                />
                              }
                              sx={{ color: 'text.secondary', fontWeight: 500 }}
                            >
                              Advanced: enter credentials manually
                            </Button>
                          </Divider>
                        </Box>
                      )}
                      <Collapse in={!isGuidedSetup || !automatedConnect || showManualCredentials}>
                        {renderCredentialsForm()}
                        <Box sx={{ mt: 2.5 }}>
                          <Button variant="outlined" size="small" onClick={handleTest} disabled={isTesting} startIcon={isTesting ? <CircularProgress size={14} /> : <CheckCircle />} sx={{ fontWeight: 500 }}>
                            {isTesting ? 'Testing...' : 'Test Connection'}
                          </Button>
                          {testResult && (
                            <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1.5, borderRadius: 1.5 }}>{testResult.message}</Alert>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Paper>
                )}
              </Box>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>Please select a platform first.</Alert>
            )}
          </Box>
        )}

        {activeTab === settingsTab && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {isGuidedSetup && (
              <Alert severity="info">
                Step 3 of 3: Choose how this integration should sync products, inventory, and pricing.
              </Alert>
            )}

            {/* Products to sync: the store's selection, or this integration's own */}
            {perIntegrationSyncSelectionEnabled && selectionIntegrationId && (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ letterSpacing: 0.3 }}>Products to sync</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                    Every integration pushes the store's selection unless you give this one its own.
                  </Typography>
                </Box>
                <Box sx={{ px: 2.5, py: 1.5 }}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '0.75rem' }} id="products-to-sync-label">Selection</FormLabel>
                    <RadioGroup
                      aria-labelledby="products-to-sync-label"
                      value={usesOwnSelection ? 'integration' : 'organization'}
                      onChange={(e) => selectionInheritMutation.mutate(e.target.value === 'organization')}
                    >
                      <FormControlLabel
                        value="organization"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">Same as the store's selection</Typography>}
                      />
                      <FormControlLabel
                        value="integration"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">This integration only</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                  {usesOwnSelection && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {integrationSyncSelection?.selectedProductIds?.length || 0} products in this integration's selection
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/app/products?integrationId=${encodeURIComponent(selectionIntegrationId)}`)}
                      >
                        Choose products
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Lightspeed eCom (flag lightspeed_ecom_connector): content-only, so the generic data-sync switches do not apply. */}
            {formData.platform === 'lightspeed_ecom' && (
              <>
                <LightspeedEcomSettingsSection
                  settings={formData.settings}
                  onChange={(partial) => setFormData({ ...formData, settings: { ...formData.settings, ...partial } })}
                />
                {integration?.id && <LightspeedEcomScanCard integration={integration} />}
              </>
            )}

            {/* Data Sync Options */}
            {formData.platform !== 'lightspeed_ecom' && (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ letterSpacing: 0.3 }}>{COPY.dataSync.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                  {COPY.dataSync.subtitle}
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 1 }}>
                {[
                  { key: 'syncProducts' as const, label: COPY.syncProducts.label, desc: COPY.syncProducts.desc, icon: <ShoppingCart fontSize="small" color="action" /> },
                  { key: 'syncPricing' as const, label: COPY.syncPricing.label, desc: COPY.syncPricing.desc, icon: <AttachMoney fontSize="small" color="action" /> },
                  { key: 'syncImages' as const, label: COPY.syncImages.label, desc: COPY.syncImages.desc, icon: <ImageIcon fontSize="small" color="action" /> },
                  { key: 'syncInventory' as const, label: COPY.syncInventory.label, desc: COPY.syncInventory.desc, icon: <Inventory2 fontSize="small" color="action" /> },
                ].map(({ key, label, desc, icon }, idx, arr) => (
                  <React.Fragment key={key}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
                      <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>{icon}</Box>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{desc}</Typography>
                      </Box>
                      {key === 'syncInventory' && formData.platform === 'shopify' ? (
                        <ShopifyScopeGatedSwitch
                          checked={formData.settings[key] === true}
                          onChange={(checked) => setFormData({ ...formData, settings: { ...formData.settings, [key]: checked } })}
                          lock={integration?.scopeStatus?.settingLocks?.syncInventory}
                          planLockedNotice={integration?.planLocks?.syncInventory ? COPY.planLock.syncInventory : undefined}
                          customApp={shopifyCustomApp}
                          granting={grantingShopifyScope}
                          onGrant={onGrantShopifyScope ? () => onGrantShopifyScope('inventory') : undefined}
                        />
                      ) : (
                        <Switch
                          size="small"
                          checked={key === 'syncInventory' ? formData.settings[key] === true : formData.settings[key] !== false}
                          onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, [key]: e.target.checked } })}
                        />
                      )}
                    </Box>
                    <Collapse in={key === 'syncInventory' && formData.settings.syncInventory === true}>
                      <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                        <NumberField
                          label="Default Inventory Count"
                          integer
                          min={0}
                          step={1}
                          adornment="units"
                          value={formData.settings.defaultInventoryCount === undefined ? '' : String(formData.settings.defaultInventoryCount)}
                          onChange={(val) => {
                            setFormData({
                              ...formData,
                              settings: { ...formData.settings, defaultInventoryCount: val === '' ? undefined : Number(val) },
                            });
                          }}
                          helperText={COPY.defaultInventoryCount.helper}
                          sx={{ width: 240 }}
                        />
                      </Box>
                    </Collapse>
                    <Collapse in={key === 'syncImages' && formData.settings.syncImages !== false}>
                      <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, mr: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {COPY.includeMarketingImages.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                              {COPY.includeMarketingImages.desc}
                            </Typography>
                          </Box>
                          <Switch
                            size="small"
                            checked={formData.settings.excludeMarketingImages === false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                settings: { ...formData.settings, excludeMarketingImages: !e.target.checked },
                              })
                            }
                          />
                        </Box>
                      </Box>
                    </Collapse>
                    {idx < arr.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {formData.platform === 'shopify' && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
                      <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
                        <Storefront fontSize="small" color="action" />
                      </Box>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {COPY.groupKitBodyAsVariants.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                          {COPY.groupKitBodyAsVariants.desc}
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={formData.settings.groupKitBodyAsVariants !== false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            settings: { ...formData.settings, groupKitBodyAsVariants: e.target.checked },
                          })
                        }
                        inputProps={{ 'aria-label': COPY.groupKitBodyAsVariants.label }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
            )}

            <ProPublicSyncSettingsSection
              platform={formData.platform}
              allowProPublicSync={formData.settings.allowProPublicSync === true}
              allowProPublicSyncAcknowledgedAt={formData.settings.allowProPublicSyncAcknowledgedAt}
              allowProPublicSyncAcknowledgedBy={formData.settings.allowProPublicSyncAcknowledgedBy}
              onAllowProChange={(v) =>
                setFormData({
                  ...formData,
                  settings: { ...formData.settings, allowProPublicSync: v },
                })
              }
            />

            {/* Tags & Automation */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ letterSpacing: 0.3 }}>{COPY.tagsAutomation.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                  {COPY.tagsAutomation.subtitle}
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}><LocalOffer fontSize="small" color="action" /></Box>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{COPY.addMotzuTag.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{COPY.addMotzuTag.desc}</Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={formData.settings.addMotzuTag !== false}
                    onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, addMotzuTag: e.target.checked } })}
                  />
                </Box>
                {formData.platform === 'shopify' && (
                  <ShopifyTagModeControl
                    tagMode={formData.settings.tagMode}
                    onChange={(tagMode) => setFormData({ ...formData, settings: { ...formData.settings, tagMode } })}
                  />
                )}
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}><Schedule fontSize="small" color="action" /></Box>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{COPY.autoSync.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{COPY.autoSync.desc}</Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={formData.settings.autoSync}
                    onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, autoSync: e.target.checked } })}
                  />
                </Box>
                <Collapse in={formData.settings.autoSync}>
                  <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                    <FormControl size="small" sx={{ width: 200 }}>
                      <InputLabel>Sync Interval</InputLabel>
                      <Select
                        value={formData.settings.syncInterval}
                        label="Sync Interval"
                        onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, syncInterval: e.target.value as any } })}
                      >
                        <MenuItem value="manual">Manual</MenuItem>
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
                      {COPY.syncInterval.helper}
                    </Typography>
                  </Box>
                </Collapse>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 1.25 }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{COPY.autoSyncNewProducts.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{COPY.autoSyncNewProducts.desc}</Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={(formData.settings as any).autoSyncNewProducts || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, autoSyncNewProducts: e.target.checked } as any,
                    }))}
                  />
                </Box>
                {formData.platform === 'shopify' && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
                      <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
                        <Receipt fontSize="small" color="action" />
                      </Box>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{COPY.orderSync.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{COPY.orderSync.desc}</Typography>
                      </Box>
                      <ShopifyScopeGatedSwitch
                        checked={orderSyncEnabled}
                        onChange={setOrderSyncEnabled}
                        lock={integration?.scopeStatus?.settingLocks?.orderSync}
                        customApp={shopifyCustomApp}
                        granting={grantingShopifyScope}
                        onGrant={onGrantShopifyScope ? () => onGrantShopifyScope('orders') : undefined}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Paper>

            {formData.platform === 'lightspeed' && lightspeedModes && (
              <>
                <SyncModeSection
                  settings={formData.settings}
                  onChange={(partial) => setFormData({ ...formData, settings: { ...formData.settings, ...partial } })}
                  modes={lightspeedModes}
                  copyVariant="lightspeed"
                  linkOnlyPricingDefaultsOn={lightspeedCatalogParityEnabled}
                />
                {lightspeedLinkOnlyEnabled && integration?.id && (
                  <LightspeedCatalogMatch integrationId={integration.id} compact />
                )}
              </>
            )}
            {formData.platform === 'lightspeed' &&
              isOrgFlagEnabled('lightspeed_sales_sync') &&
              (lightspeedCatalogParityEnabled || isOrgFlagEnabled('lightspeed_store_config')) && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <LightspeedDiscountChoice
                    checked={formData.settings.rebateDiscounts?.instant !== false}
                    onChange={(instant) =>
                      setFormData({
                        ...formData,
                        settings: { ...formData.settings, rebateDiscounts: { ...formData.settings.rebateDiscounts, instant } },
                      })
                    }
                  />
                </Paper>
              )}

            {formData.platform === 'shopify' && (
              <>
                <ShopifySetupChecklist
                  shopId={formData.credentials?.shopId ? String(formData.credentials.shopId) : null}
                  integrationId={integration?.id || null}
                />
                {integration?.id && <ShopifyPricingZonesSection integrationId={integration.id} />}
                {integration?.id && (
                  <>
                    <StorefrontPresetGallery
                      integrationId={integration.id}
                      storeName={formData.name || integration.name}
                      appliedPreset={formData.settings.appliedStorefrontPreset || integration.settings.appliedStorefrontPreset}
                      onApplied={(applied) => {
                        setFormData((prev) => ({
                          ...prev,
                          settings: { ...prev.settings, appliedStorefrontPreset: applied },
                        }));
                        queryClient.invalidateQueries({ queryKey: ['integrations'] });
                      }}
                    />
                    <StorefrontPresetsCard
                      integrationId={integration.id}
                      lastSync={integration.lastSync}
                      motzuMetafields={formData.settings.motzuMetafields ?? integration.settings?.motzuMetafields}
                      customApp={shopifyCustomApp}
                      onMotzuMetafieldsSaved={(map) => {
                        setFormData((prev) => ({ ...prev, settings: { ...prev.settings, motzuMetafields: map } }));
                      }}
                    />
                  </>
                )}

                {/* Sync Mode */}
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SyncAlt fontSize="small" color="action" />
                      <Typography variant="subtitle2" sx={{ letterSpacing: 0.3 }}>{COPY.syncMode.title}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                      {COPY.syncMode.subtitle}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {([
                        { value: 'link_only' as const, label: COPY.syncMode.linkOnlyShopify.label, desc: COPY.syncMode.linkOnlyShopify.desc, icon: <LinkIcon fontSize="small" /> },
                        { value: 'enrich' as const, label: COPY.syncMode.enrich.label, desc: COPY.syncMode.enrich.desc, icon: <Add fontSize="small" /> },
                        { value: 'override' as const, label: COPY.syncMode.override.label, desc: COPY.syncMode.override.desc, icon: <SyncAlt fontSize="small" /> },
                      ] as const).map(({ value, label, desc, icon }) => {
                        const selected = (formData.settings.syncMode || 'override') === value;
                        return (
                          <Paper
                            key={value}
                            variant="outlined"
                            onClick={() => setFormData({ ...formData, settings: { ...formData.settings, syncMode: value } })}
                            sx={{
                              flex: 1,
                              p: 1.5,
                              cursor: 'pointer',
                              borderWidth: selected ? 2 : 1,
                              borderColor: selected ? 'primary.main' : 'divider',
                              bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : 'transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': { borderColor: selected ? 'primary.main' : 'grey.400' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: selected ? 'primary.main' : 'text.secondary' }}>
                              {icon}
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{desc}</Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                    {(formData.settings.syncMode || 'override') === 'override' && (
                      <OverrideFieldsPicker
                        value={formData.settings.overrideFields}
                        onChange={(overrideFields) => setFormData({ ...formData, settings: { ...formData.settings, overrideFields } })}
                        imagesSyncEnabled={formData.settings.syncImages !== false}
                      />
                    )}
                    {/* Beside the sync mode, hidden in link-only exactly as in SyncModeSection: nothing is pushed there. */}
                    {formData.platform === 'shopify' && (formData.settings.syncMode || 'override') !== 'link_only' && (
                      <Box sx={{ mt: 1.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <ContinueSellingRow
                          checked={formData.settings.continueSellingWhenOutOfStock === true}
                          onChange={(continueSellingWhenOutOfStock) =>
                            setFormData({
                              ...formData,
                              settings: { ...formData.settings, continueSellingWhenOutOfStock },
                            })
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </Paper>

                <ShopifyDescriptionTemplateSection
                  settings={formData.settings}
                  integrationId={integration?.id}
                  onChange={(partial) =>
                    setFormData((prev) => ({ ...prev, settings: { ...prev.settings, ...partial } }))
                  }
                />

                {/* Product Organization: the same editor as the integration page's Mapping tab */}
                <ShopifyCategoryMappingSection
                  integrationId={integration?.id}
                  layout={formData.settings.storeLayout}
                  onChange={(storeLayout) =>
                    setFormData((prev) => ({ ...prev, settings: { ...prev.settings, storeLayout } }))
                  }
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {isGuidedSetup ? (
          <>
            <Button onClick={() => onTabChange(credentialsTab)} disabled={activeTab === credentialsTab}>
              Back
            </Button>
            {activeTab === credentialsTab ? (
              <Button onClick={() => onTabChange(settingsTab)} variant="contained">
                {t('integrations.detailTabs.continueToSyncBehavior')}
              </Button>
            ) : (
              <Button onClick={handleSave} variant="contained">
                Save Integration
              </Button>
            )}
          </>
        ) : (
          <Button onClick={handleSave} variant="contained" sx={{ px: 3 }}>
            {integration ? 'Save Changes' : 'Create Integration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
